<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import type { StatItem } from '@/types/StatGroup.types';
import Skeleton from 'primevue/skeleton';

interface Props {
    title?: string
    items: StatItem[]
    columns?: number
    loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    title: '',
    columns: 2,
    loading: false,
});

const rows = computed(() => {
    const result: StatItem[][] = [];
    for (let i = 0; i < props.items.length; i += props.columns) {
        result.push(props.items.slice(i, i + props.columns));
    }
    return result;
});
</script>

<template>
    <section class="stat-group">
        <h2 v-if="title" class="stat-group__title">{{ title }}</h2>

        <div class="stat-group__grid" :style="{ '--stat-columns': columns }">
            <div
                v-for="(row, rowIndex) in rows"
                :key="rowIndex"
                class="stat-group__row"
                :class="{ 'stat-group__row--divided': rowIndex > 0 }"
            >
                <component
                    :is="item.to ? RouterLink : 'div'"
                    v-for="(item, colIndex) in row"
                    :key="item.id"
                    class="stat-group__cell"
                    :class="{ 'stat-group__cell--divided': colIndex > 0, 'stat-group__cell--clickable': !!item.to }"
                    :to="item.to ?? undefined"
                >
                    <div class="stat-group__cell-body">
                        <span class="stat-group__label">{{ item.label }}</span>
                        <Skeleton v-if="loading" width="40%" height="36px" border-radius="6px" />
                        <span v-else class="stat-group__value">{{ item.value }}</span>
                    </div>

                    <div
                        v-if="$slots.icon || item.icon || item.iconComponent"
                        class="stat-group__icon-wrap"
                        aria-hidden="true"
                    >
                        <slot name="icon" :item="item">
                            <component :is="item.iconComponent" v-if="item.iconComponent" class="stat-group__icon" />
                            <i v-else :class="item.icon" class="stat-group__icon" />
                        </slot>
                    </div>
                </component>
            </div>
        </div>
    </section>
</template>

<style lang="scss" scoped>
.stat-group {
    &__title {
        margin: 0 30px 20px;
        font-family: $font-family-archivo;
        font-size: 20px;
        font-weight: 600;
        line-height: 1;
        color: rgba($black-color, 0.75);
    }

    &__grid {
        border: 1px solid $border-gray-color;
        border-radius: 20px;
        overflow: hidden;
        background: $white-color;
    }

    &__row {
        display: flex;
        flex-wrap: wrap;

        &--divided {
            border-top: 1px solid $border-gray-color;
        }
    }

    &__cell {
        display: flex;
        flex: 0 0 calc(100% / var(--stat-columns));
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 22px 28px;
        min-width: 0;
        text-decoration: none;
        color: inherit;

        &--divided {
            border-left: 1px solid $border-gray-color;
        }

        &--clickable {
            cursor: pointer;
        }
    }

    &__cell-body {
        display: flex;
        flex-direction: column;
        gap: 15px;
        min-width: 0;
    }

    &__label {
        font-family: $font-family-archivo;
        font-size: 16px;
        font-weight: 400;
        line-height: 1;
        color: rgba($black-color, 0.75);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__value {
        font-family: $font-family-archivo;
        font-size: 36px;
        font-weight: 600;
        line-height: 1;
        color: $black-color;
    }

    &__icon-wrap {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        width: 70px;
        height: 70px;
        border-radius: 20px;
        background: $border-gray-color;
    }

    &__icon {
        font-size: 30px;
        color: $black-color;
    }
}
</style>
